import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { type PageViewEvent, pageViewEvent } from "./events";
import { useLastPageView } from "./use-last-page-view";
import { getUserSessionStatus } from "./user-session-cookie";

const useHrefChangeEffect = (callback: () => void, _deps: unknown[] = []) => {
  const _location = useLocation();

  useEffect(() => {
    callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callback can be invoked only for href parts changes.
  }, [callback]);
};

type UseTrackPageViewsOptions = {
  additionalData?: Record<string, unknown>;
  onPageView?: () => void;
};

export const useTrackPageViews = (options?: Record<string, unknown> | UseTrackPageViewsOptions) => {
  const { additionalData, onPageView } = isOptionsObject(options)
    ? options
    : { additionalData: options, onPageView: undefined };

  const referrer = useRef(typeof window === "undefined" ? "" : window.document.referrer);
  const isInitialMount = useRef(true);
  const previousPathname = useRef<string | null>(null);
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedInitial = useRef(false);
  const { status } = getUserSessionStatus();
  const { getLastPageView, setLastPageView } = useLastPageView();
  const onPageViewRef = useRef(onPageView);
  onPageViewRef.current = onPageView;

  const trackPageView = () => {
    if (typeof window !== "undefined") {
      const lastPageView = getLastPageView();

      const eventPayload: PageViewEvent = {
        page_path: window.location.pathname,
        page_location: window.location.href,
        page_referrer: referrer.current,
        page_title: window.document.title,
        user_id: lastPageView?.user_id || null,
        is_logged: lastPageView?.is_logged || false,
        user_ordered_visits: lastPageView?.user_ordered_visits || 0,
        selected_city: lastPageView?.selected_city || null,
        selected_address: lastPageView?.selected_address || null,
        geo_ip_city: lastPageView?.geo_ip_city || null,
        zone_id: lastPageView?.zone_id || null,
        cart_id: lastPageView?.cart_id || null,
        customer_type: status,
      };

      setLastPageView(eventPayload);
      pageViewEvent({ ...additionalData, ...eventPayload });
      onPageViewRef.current?.();

      referrer.current = window.location.href;
      previousPathname.current = window.location.pathname;
    }
  };

  useHrefChangeEffect(() => {
    if (typeof window !== "undefined") {
      const currentPathname = window.location.pathname;

      // On initial mount, always delay tracking to ensure GTM/CMP consent layer
      // is ready before pushing the first page_view event.
      // Without the delay, events pushed synchronously from useEffect are lost
      // because GTM's consent mode (wait_for_update) hasn't resolved yet.
      if (isInitialMount.current) {
        isInitialMount.current = false;
        previousPathname.current = currentPathname;

        pendingTimeout.current = setTimeout(() => {
          if (!hasTrackedInitial.current) {
            trackPageView();
            hasTrackedInitial.current = true;
          }
        }, 100);
        return;
      }

      if (previousPathname.current !== currentPathname) {
        if (pendingTimeout.current) {
          clearTimeout(pendingTimeout.current);
          pendingTimeout.current = null;
        }
        trackPageView();
        hasTrackedInitial.current = true;
      }
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingTimeout.current) {
        clearTimeout(pendingTimeout.current);
      }
    };
  }, []);
};

function isOptionsObject(
  value: Record<string, unknown> | UseTrackPageViewsOptions | undefined,
): value is UseTrackPageViewsOptions {
  if (!value) return false;
  return "additionalData" in value || "onPageView" in value;
}
